import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { 
  fetchTelegramStatus, 
  generateTelegramLink, 
  unlinkTelegram,
  clearError,
  clearLinkData 
} from '../store/slices/telegramSlice';
import LoadingSpinner from './LoadingSpinner';
import { formatDateDisplay } from '../utils/dateUtils';
import './Settings.css';

const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { status, linkData, loading, error } = useAppSelector((state) => state.telegram);
  
  const [showLinkInstructions, setShowLinkInstructions] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  useEffect(() => {
    dispatch(fetchTelegramStatus());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleGenerateLink = async () => {
    dispatch(clearError());
    const result = await dispatch(generateTelegramLink());
    if (generateTelegramLink.fulfilled.match(result)) {
      setShowLinkInstructions(true);
    }
  };

  const handleUnlink = async () => {
    if (window.confirm('Are you sure you want to unlink your Telegram account? You will no longer receive notifications.')) {
      dispatch(unlinkTelegram());
    }
  };

  const handleCloseLinkInstructions = () => {
    setShowLinkInstructions(false);
    setCopiedToClipboard(false); // Reset copied state when closing modal
    dispatch(clearLinkData());
    // Refresh status to check if linking was successful
    dispatch(fetchTelegramStatus());
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToClipboard(true);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedToClipboard(false);
      }, 2000);
    }).catch(() => {
      // Handle copy failure silently - could add error state here if needed
    });
  };

  if (loading && !status) {
    return <LoadingSpinner />;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings and integrations</p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* User Profile Section */}
      <div className="settings-section">
        <h2>Profile Information</h2>
        <div className="profile-info">
          <div className="info-row">
            <label>Name:</label>
            <span>{user?.firstName} {user?.lastName}</span>
          </div>
          <div className="info-row">
            <label>Email:</label>
            <span>{user?.email}</span>
          </div>
          <div className="info-row">
            <label>Role:</label>
            <span className={`role-badge ${user?.role}`}>{user?.role}</span>
          </div>
        </div>
      </div>

      {/* Telegram Integration Section */}
      <div className="settings-section">
        <h2>Telegram Integration</h2>
        <p className="section-description">
          Link your Telegram account to receive important notifications and updates directly in Telegram.
        </p>

        {status?.isLinked ? (
          <div className="telegram-linked">
            <div className="linked-info">
              <div className="linked-details">
                <strong><span className="status-icon">✅</span>Telegram account linked successfully!</strong>
                {status.telegramUsername && (
                  <p>Username: @{status.telegramUsername}</p>
                )}
                {status.linkedAt && (
                  <p>Linked on: {formatDateDisplay(status.linkedAt, true)}</p>
                )}
              </div>
            </div>
            <button 
              className="unlink-button"
              onClick={handleUnlink}
              disabled={loading}
            >
              {loading ? 'Unlinking...' : 'Unlink Telegram'}
            </button>
          </div>
        ) : (
          <div className="telegram-not-linked">
            <div className="not-linked-info">
              <div>
                <strong><span className="status-icon">❌</span>Telegram not linked</strong>
                <p>Connect your Telegram account to receive notifications</p>
              </div>
            </div>
            <button 
              className="link-button"
              onClick={handleGenerateLink}
              disabled={loading}
            >
              {loading ? 'Generating Link...' : 'Link Telegram Account'}
            </button>
          </div>
        )}
      </div>

      {/* Link Instructions Modal */}
      {showLinkInstructions && linkData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Link Your Telegram Account</h3>
              <button 
                className="close-button"
                onClick={handleCloseLinkInstructions}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="instructions">
                <p className="instructions-text">
                  {linkData.instructions}
                </p>
                
                <div className="link-container">
                  <div className="link-display">
                    <code>{linkData.chatLink}</code>
                    <button 
                      className={`copy-button ${copiedToClipboard ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(linkData.chatLink)}
                      disabled={copiedToClipboard}
                    >
                      {copiedToClipboard ? '✅ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                </div>

                <div className="step-by-step">
                  <h4>Step-by-step instructions:</h4>
                  <ol>
                    <li>Click the link above or copy it to your browser</li>
                    <li>This will open Telegram and start a chat with Clara Manager Bot</li>
                    <li>Click "START" in Telegram to complete the linking process</li>
                    <li>You'll receive a confirmation message when linking is successful</li>
                  </ol>
                </div>

                <div className="security-note">
                  <strong>⚠️ Security Note:</strong> This link is unique to your account and expires in 1 hour. 
                  Don't share it with anyone else.
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <a 
                href={linkData.chatLink}
                target="_blank"
                rel="noopener noreferrer"
                className="open-telegram-button"
              >
                📱 Open in Telegram
              </a>
              <button 
                className="done-button"
                onClick={handleCloseLinkInstructions}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 